import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Calendar, X, Play, Pause, SkipBack, SkipForward } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Post } from "@/hooks/usePosts";

interface TimeLapseProps {
  crop: string;
  posts: Post[];
  onClose: () => void;
}

export const TimeLapse = ({ crop, posts, onClose }: TimeLapseProps) => {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const cropVideos = posts.filter(post => 
    post.crop === crop && post.isVideo
  ).sort((a, b) => new Date(a.created_at || a.timestamp).getTime() - new Date(b.created_at || b.timestamp).getTime());

  const currentVideo = cropVideos[currentVideoIndex];

  const handleNext = () => {
    if (currentVideoIndex < cropVideos.length - 1) {
      setCurrentVideoIndex(currentVideoIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentVideoIndex > 0) {
      setCurrentVideoIndex(currentVideoIndex - 1);
    }
  };

  const handlePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const formatDate = (timestamp: string) => {
    let date: Date;
    
    if (timestamp === 'Just now') {
      date = new Date();
    } else if (timestamp.includes('hour')) {
      const hours = parseInt(timestamp.split(' ')[0]);
      date = new Date(Date.now() - hours * 60 * 60 * 1000);
    } else if (timestamp.includes('day')) {
      const days = parseInt(timestamp.split(' ')[0]);
      date = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
    } else {
      try {
        date = new Date(timestamp);
        if (isNaN(date.getTime())) {
          date = new Date();
        }
      } catch {
        date = new Date();
      }
    }
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getSentimentColor = (sentiment?: string) => {
    switch (sentiment) {
      case 'great':
        return 'bg-gradient-to-r from-green-500 to-green-600';
      case 'help':
        return 'bg-gradient-to-r from-red-500 to-red-600';
      default:
        return 'bg-gradient-to-r from-blue-500 to-blue-600';
    }
  };

  const getSentimentText = (sentiment?: string) => {
    switch (sentiment) {
      case 'great':
        return '🎉 Going Great';
      case 'help':
        return '🆘 Need Help';
      default:
        return '📈 Update';
    }
  };

  if (!mounted) return null;

  if (cropVideos.length === 0) {
    return createPortal(
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-md w-full p-6 text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-4">No Time Lapse Available</h2>
          <p className="text-gray-600 mb-4">
            No video posts found for {crop}. Start creating video updates to build your time lapse!
          </p>
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[95vh] overflow-y-auto m-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">
            {crop} Time Lapse ({cropVideos.length} videos)
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Video Player */}
        <div className="relative">
          <video
            key={currentVideo.id}
            src={currentVideo.image}
            className="w-full h-64 md:h-96 object-cover"
            controls={false}
            autoPlay={isPlaying}
            muted
            playsInline
            loop={false}
          />
          
          {/* Video Overlay with stronger background */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30 pointer-events-none" />
          
          {/* Crop Name - Top Center */}
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10">
            <div className="bg-black bg-opacity-80 text-white px-4 py-2 rounded-full text-lg font-bold shadow-lg">
              {currentVideo.crop}
            </div>
          </div>

          {/* Sentiment Badge - Top Right */}
          <div className="absolute top-4 right-4 z-10">
            <div className={`${getSentimentColor(currentVideo.sentiment)} text-white px-4 py-2 rounded-full text-sm font-bold shadow-xl border-2 border-white/20 whitespace-nowrap`}>
              {getSentimentText(currentVideo.sentiment)}
            </div>
          </div>
          
          {/* Date and Content - Bottom Left */}
          <div className="absolute bottom-4 left-4 text-white z-10 max-w-md">
            <div className="bg-black bg-opacity-80 px-3 py-2 rounded-lg mb-2">
              <div className="text-sm font-medium mb-1">
                📅 {formatDate(currentVideo.created_at || currentVideo.timestamp)}
              </div>
              <p className="text-sm leading-relaxed">{currentVideo.content}</p>
              
              {/* Additional Info */}
              {currentVideo.whatsHappening && (
                <div className="mt-2 text-xs text-blue-200">
                  <strong>What's happening:</strong> {currentVideo.whatsHappening}
                </div>
              )}
              
              {currentVideo.challenges && (
                <div className="mt-1 text-xs text-yellow-200">
                  <strong>Challenges:</strong> {currentVideo.challenges}
                </div>
              )}
            </div>
          </div>

          {/* Progress Indicator - Bottom Right */}
          <div className="absolute bottom-4 right-4 bg-black bg-opacity-80 text-white px-3 py-2 rounded-full text-sm font-medium z-10">
            {currentVideoIndex + 1} / {cropVideos.length}
          </div>
        </div>

        {/* Controls */}
        <div className="p-4 bg-gray-50 space-y-4">
          {/* Playback Controls */}
          <div className="flex items-center justify-center space-x-4">
            <Button
              onClick={handlePrevious}
              disabled={currentVideoIndex === 0}
              variant="outline"
              size="sm"
            >
              <SkipBack size={16} />
            </Button>
            
            <Button
              onClick={handlePlay}
              variant="outline"
              size="sm"
            >
              {isPlaying ? <Pause size={16} /> : <Play size={16} />}
            </Button>
            
            <Button
              onClick={handleNext}
              disabled={currentVideoIndex === cropVideos.length - 1}
              variant="outline"
              size="sm"
            >
              <SkipForward size={16} />
            </Button>
          </div>

          {/* Timeline Slider */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Timeline</label>
            <Slider
              value={[currentVideoIndex]}
              onValueChange={(value) => setCurrentVideoIndex(value[0])}
              max={cropVideos.length - 1}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>{cropVideos.length > 0 ? formatDate(cropVideos[0].created_at || cropVideos[0].timestamp) : ''}</span>
              <span>{cropVideos.length > 0 ? formatDate(cropVideos[cropVideos.length - 1].created_at || cropVideos[cropVideos.length - 1].timestamp) : ''}</span>
            </div>
          </div>

          {/* Video Grid Preview */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">All Videos</label>
            <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 max-h-32 overflow-y-auto">
              {cropVideos.map((video, index) => (
                <button
                  key={video.id}
                  onClick={() => setCurrentVideoIndex(index)}
                  className={`relative aspect-video rounded-lg overflow-hidden border-2 transition-all ${
                    index === currentVideoIndex 
                      ? 'border-green-500 scale-105' 
                      : 'border-gray-300 hover:border-green-300'
                  }`}
                >
                  <video
                    src={video.image}
                    className="w-full h-full object-cover"
                    muted
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
                    <Play size={12} className="text-white" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 truncate">
                    {formatDate(video.created_at || video.timestamp)}
                  </div>
                 </button>
               ))}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
